import { CALLING_CODES } from '#contracts/calling-codes'

/**
 * Phone numbers, stored in one shape: E.164, `+6281234567890`.
 *
 * One shape because a number is dialed, messaged and deduplicated, and each of those breaks on
 * `0812-3456-7890` from one desk and `+62 812 3456 7890` from another. The picker shows a
 * country and its calling code beside the national digits; what reaches the database is always
 * the joined form.
 *
 * Checked against each country's possible national lengths, not against a full numbering plan.
 * That rejects the typo that loses or doubles a digit, which is the mistake people make, while
 * never refusing a real number because a carrier opened a range the metadata has not heard of.
 */

export type PhoneParts = { country: string; national: string }

export function callingCode(country: string): string | undefined {
  return CALLING_CODES[country]?.[0]
}

/** Digits only. Spaces, dashes, dots and brackets are how people write numbers, not the number. */
function digits(text: string): string {
  return text.replaceAll(/\D/g, '')
}

/**
 * The regions a calling code belongs to, main one first.
 *
 * Longest code first is not needed: calling codes are a prefix code (ITU E.164 guarantees no code
 * is a prefix of another), so the first to match is the only one that can.
 */
function regionsFor(e164Digits: string): { code: string; regions: string[] } | undefined {
  for (const length of [1, 2, 3]) {
    const code = e164Digits.slice(0, length)
    const regions = Object.entries(CALLING_CODES)
      .filter(([, row]) => row[0] === code)
      .toSorted(([, a], [, b]) => Number(b[4] === true) - Number(a[4] === true))
      .map(([region]) => region)
    if (regions.length > 0) return { code, regions }
  }
  return undefined
}

/**
 * Split a stored number back into its country and national digits, for editing.
 *
 * `hint` is the country the editor last had selected. `+1 415…` is the United States unless the
 * form says Canada, so an operator correcting a Canadian number does not see the picker jump.
 */
export function parsePhone(e164: string, hint?: string): PhoneParts | undefined {
  if (!e164.startsWith('+')) return undefined
  const all = digits(e164)
  const found = regionsFor(all)
  if (found === undefined) return undefined
  const [first] = found.regions
  if (first === undefined) return undefined
  const country = hint !== undefined && found.regions.includes(hint) ? hint : first
  return { country, national: all.slice(found.code.length) }
}

/**
 * The stored form, from a country and whatever was typed after its code.
 *
 * A leading national prefix is dropped: in Indonesia a mobile is written `0812…` and dialed from
 * abroad as `+62 812…`, and people type the form they know.
 */
export function joinPhone(country: string, national: string): string {
  const row = CALLING_CODES[country]
  const typed = digits(national)
  if (row === undefined) return `+${typed}`
  const prefix = row[3]
  const local = prefix !== '' && typed.startsWith(prefix) ? typed.slice(prefix.length) : typed
  return `+${row[0]}${local}`
}

/** Why a stored-form number is wrong, or `undefined` when it is fine. */
export function phoneProblem(value: string, hint?: string): string | undefined {
  if (!/^\+\d{4,18}$/.test(value)) return 'Enter the number with its country code.'
  const parts = parsePhone(value, hint)
  if (parts === undefined) return 'That country code does not exist.'
  const row = CALLING_CODES[parts.country]
  if (row === undefined) return 'That country code does not exist.'
  const [, min, max] = row
  if (parts.national.length < min) return 'That number is too short.'
  if (parts.national.length > max) return 'That number is too long.'
  return undefined
}

export type PhoneResult = { ok: true; value: string } | { ok: false; message: string }

/**
 * A number from free text, in the stored form — for imports and for any caller that did not use
 * the picker.
 *
 * `+62 812-3456-7890` and `0062 812 3456 7890` are read as international. Anything else is a
 * national number in `defaultCountry`, the edition's own, which is what a spreadsheet of local
 * delegates contains.
 */
export function normalizePhone(raw: string, defaultCountry: string): PhoneResult {
  const trimmed = raw.trim()
  let value: string
  if (trimmed.startsWith('+')) value = `+${digits(trimmed)}`
  else if (trimmed.startsWith('00')) value = `+${digits(trimmed).slice(2)}`
  else value = joinPhone(defaultCountry, trimmed)

  const problem = phoneProblem(value, defaultCountry)
  return problem === undefined ? { ok: true, value } : { ok: false, message: problem }
}

/**
 * For reading: `+62 812 3456 7890`.
 *
 * Groups of four from the right after the calling code. Not every country writes numbers that
 * way, but a spaced number is far easier to read back over the phone than fourteen digits in a
 * row, and the stored value is untouched.
 */
export function displayPhone(value: string): string {
  const parts = parsePhone(value)
  if (parts === undefined) return value
  const code = callingCode(parts.country) ?? ''
  const groups: string[] = []
  let rest = parts.national
  while (rest.length > 4) {
    groups.unshift(rest.slice(-4))
    rest = rest.slice(0, -4)
  }
  if (rest !== '') groups.unshift(rest)
  return `+${code} ${groups.join(' ')}`
}
